import { createReducer } from 'src/helpers/reducer';
import actionTypes from './actionTypes';
import type {
  IDownloadTemplateRequestPayload,
  ILoadEngineSuccessPayload,
  ILoadVersionSuccessPayload,
  IViewRateEngineState,
} from './types';

const initState: IViewRateEngineState = {
  engine: null,
  loading: false,
  error: false,
  selectedVersion: null,
  isLoadingVersion: false,
  versionError: false,
  downloadingTemplate: null,
};

function onLoadEngineRequest(state: IViewRateEngineState): IViewRateEngineState {
  return {
    ...state,
    loading: true,
    error: false,
    engine: null,
    selectedVersion: null,
    versionError: false,
  };
}

function onLoadEngineSuccess(
  state: IViewRateEngineState,
  { payload }: { payload: ILoadEngineSuccessPayload },
): IViewRateEngineState {
  return {
    ...state,
    loading: false,
    error: false,
    engine: payload.engine,
  };
}

function onLoadEngineError(state: IViewRateEngineState): IViewRateEngineState {
  return {
    ...state,
    loading: false,
    error: true,
  };
}

function onLoadVersionRequest(state: IViewRateEngineState): IViewRateEngineState {
  // Drop the outgoing structure so a stale one is never shown under the new version's heading.
  return { ...state, isLoadingVersion: true, versionError: false, selectedVersion: null };
}

function onLoadVersionSuccess(
  state: IViewRateEngineState,
  { payload }: { payload: ILoadVersionSuccessPayload },
): IViewRateEngineState {
  return { ...state, isLoadingVersion: false, versionError: false, selectedVersion: payload.version };
}

function onLoadVersionError(state: IViewRateEngineState): IViewRateEngineState {
  return { ...state, isLoadingVersion: false, versionError: true, selectedVersion: null };
}

function onDownloadTemplateRequest(
  state: IViewRateEngineState,
  { payload }: { payload: IDownloadTemplateRequestPayload },
): IViewRateEngineState {
  return { ...state, downloadingTemplate: payload };
}

// Two downloads can be in flight at once and the later one owns the pending state, so an earlier
// one settling must not clear it.
function onDownloadTemplateSettled(
  state: IViewRateEngineState,
  { payload }: { payload: IDownloadTemplateRequestPayload },
): IViewRateEngineState {
  const pending = state.downloadingTemplate;

  if (pending?.versionId !== payload.versionId || pending?.side !== payload.side) return state;

  return { ...state, downloadingTemplate: null };
}

export default createReducer(initState, {
  [actionTypes.LOAD_ENGINE_REQUEST]: onLoadEngineRequest,
  [actionTypes.LOAD_ENGINE_SUCCESS]: onLoadEngineSuccess,
  [actionTypes.LOAD_ENGINE_ERROR]: onLoadEngineError,
  [actionTypes.LOAD_VERSION_REQUEST]: onLoadVersionRequest,
  [actionTypes.LOAD_VERSION_SUCCESS]: onLoadVersionSuccess,
  [actionTypes.LOAD_VERSION_ERROR]: onLoadVersionError,
  [actionTypes.DOWNLOAD_TEMPLATE_REQUEST]: onDownloadTemplateRequest,
  [actionTypes.DOWNLOAD_TEMPLATE_SETTLED]: onDownloadTemplateSettled,
  [actionTypes.CLEAR_STORE]: () => initState,
});
